import { type ChangeEvent, useCallback, useState } from 'react';

import { useDebounce } from '@uidotdev/usehooks';
import { LoaderCircle, Search, SearchX } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import AISparkles from '@/components/common/AISparkles';
import SubmitButton from '@/components/common/SubmitButton';
import type { ModalProps } from '@/components/providers/ModalProvider/ModalProvider';
import { Input } from '@/components/shadcn/input';
import SelectParty from '@/features/party/components/SelectParty/SelectParty';
import useParties from '@/features/party/queries/hooks/useParties';
import type { Party } from '@/features/party/types';
import useAuth from '@/hooks/useAuth';
import hasItems from '@/lib/utils/hasItems';
import isEmpty from '@/lib/utils/isEmpty';

import useCreateParty from '../../mutations/useCreateNaturalParty';

type Props = {
  onSubmit: (partyId: string) => void;
};

export default function PartySearchModal({
  onSubmit,
  onClose,
}: ModalProps<Props>) {
  const { t } = useTranslation();
  const { distribution } = useAuth();
  const [search, setSearch] = useState('');
  const debouncedSearch = useDebounce(search, 300);

  const handleChange = useCallback(
    (e: ChangeEvent<HTMLInputElement>) => setSearch(e.target.value),
    [],
  );

  const { data: parties, isLoading } = useParties(
    distribution?.id ?? '',
    { query: debouncedSearch },
    {
      enabled: Boolean(distribution?.id) && !isEmpty(debouncedSearch),
    },
  );

  const handleSubmit = useCallback(
    (party: Party) => {
      onSubmit(party.id);
      onClose();
    },
    [onClose, onSubmit],
  );

  const { mutateAsync: createParty } = useCreateParty();

  const handleCreateParty = async () => {
    const isLikelyEmail = /.+@.+\..+/.test(search);

    const newParty = isLikelyEmail
      ? {
          firstName: search.split('@')[0] ?? '',
          email: search,
        }
      : {
          firstName: search,
          email: '',
        };

    const result = await createParty(newParty);

    onSubmit(result.id);
    onClose();
  };

  return (
    <div className="flex h-full flex-col">
      <div className="p-2">
        <Input
          value={search}
          onChange={handleChange}
          placeholder={t('PARTY_SEARCH_PLACEHOLDER')}
        />
      </div>

      {isLoading && (
        <div className="flex h-full items-center justify-center p-2">
          <LoaderCircle className="text-primary-500 animate-spin" />
        </div>
      )}

      {!isLoading && (
        <>
          {hasItems(parties) && (
            <div className="h-full overflow-y-auto">
              <div className="p-2">
                <SelectParty parties={parties} onSubmit={handleSubmit} />
              </div>
            </div>
          )}

          {!hasItems(parties) && !isEmpty(debouncedSearch) && (
            <div className="flex flex-col items-center justify-start gap-2 p-2">
              <SearchX className="text-primary-500 h-8 w-8" />

              <div className="flex flex-col">
                <p className="text-foreground text-center text-sm">
                  {t('PARTY_SEARCH_NO_RESULTS')}
                </p>

                <p className="text-foreground/50 text-center text-xs">
                  {t('PARTY_CREATE_FROM_QUERY_DESC')}
                </p>
              </div>

              <SubmitButton
                size="sm"
                className="mt-2"
                onSubmit={handleCreateParty}
              >
                <AISparkles className="text-inherit" />

                {t('PARTY_CREATE_FROM_QUERY')}
              </SubmitButton>
            </div>
          )}

          {!hasItems(parties) && isEmpty(debouncedSearch) && (
            <div className="flex flex-col items-center justify-start gap-2 p-2">
              <Search className="text-primary-500 h-8 w-8" />

              <div className="flex flex-col items-center justify-center">
                <p className="text-foreground text-center text-sm">
                  {t('PARTY_SEARCH_TYPE_SOMETHING_TEXT')}
                </p>

                <p className="text-foreground/50 text-center text-xs">
                  {t('PARTY_SEARCH_TYPE_SOMETHING_SUBTEXT')}
                </p>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}
